import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withNamespaces } from 'react-i18next';

import signantActions from '../../store/actions/3rd/signant.actions';
import * as Utils from '../../utils/Utils';

import Loading from '../UI/Loading';
import ErrorMessage from '../UI/ErrorMessage';
import SignNextBankAuthorize from './SignNextBankAuthorize';

import rgbPaymentsBasketsService from '../../services/3rd/rgb/rgbPaymentsBaskets.service';
import rgbPaymentsService from '../../services/3rd/rgb/rgbPayments.service';
import voucherPaymentServices from '../../services/voucherPaymentServices';
import LoadingProgressBar from '../UI/LoadingProgressBar';
import { ErrorRoutes, RouterNoPrefix, RoutesUrl } from '../../constants/routes.constants';
import { useStyles } from '../ztl/sign/consent/styles';

const SignNext = (props) => {
  const {
    t,
    dispatch,
    history,
    isGettingAuthStatus,
    isHandlingAuthorizeCompanyBanks,
    authorizeCompanyBanksData,
    errors,
    errors: { handleAuthorizeCompanyBanks: errorHandleAuthorizeCompanyBanks },
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [seconds, setSeconds] = useState(5);

  const timerRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const actionRef = useRef(null);

  const classes = useStyles();

  const countDown = useCallback(() => {
    setSeconds((prev) => {
      const next = prev - 1;

      if (next <= 0) {
        clearInterval(timerRef.current);
        return 0;
      }

      return next;
    });
  }, []);

  const autoRedirectAfterCountdown = useCallback(() => {
    timerRef.current = setInterval(countDown, 1000);

    closeTimeoutRef.current = setTimeout(() => {
      const state = Utils.getParameterByName('state');
      if (state) {
        return history.push(state);
      }
      const router = Utils.rebuildRouterUrl(RoutesUrl.PaymentList);
      return history.push(router);
    }, 5000);
  }, [countDown, history]);

  const getBasketUUID = useCallback(async () => {
    const basketUUID = Utils.getParameterByName('basketUUID');

    if (!basketUUID) {
      return null;
    }

    const { success, data } = await rgbPaymentsBasketsService.getByBasketUUID({
      basketUUID,
    });

    if (!success || !data?.length) {
      return null;
    }

    return basketUUID;
  }, []);

  const handlePaymentProcess = useCallback(async () => {
    const basketUUID = await getBasketUUID();

    if (!basketUUID) {
      history.push(ErrorRoutes.Notfound);
      return;
    }

    setIsLoading(true);

    try {
      const response = await rgbPaymentsService.signingBaskets({
        basketUUID,
      });

      const { success, data } = response;

      if (success) {
        const paymentsIds = data.map((item) => item.paymentsId);

        await voucherPaymentServices.updateByPaymentsIds({
          paymentsIds,
        });

        autoRedirectAfterCountdown();
      }
    } finally {
      setIsLoading(false);
    }
  }, [getBasketUUID, history, autoRedirectAfterCountdown]);

  const handleCompanyBankVerifyProcess = useCallback(
    async ({ bankGroup }) => {
      const bankGroups = ['sparebank1', 'dnb', 'handelsbanken'];

      if (!bankGroups.includes(bankGroup)) {
        return;
      }

      let signantAuth = {};

      try {
        signantAuth = JSON.parse(localStorage.getItem('tmp-signant-auth') || '{}');
      } catch (error) {
        signantAuth = {};
      }

      const { RequestID: requestID, TrackingID: trackingID, companyId, assignUser } = signantAuth;

      const companyBankIds = Utils.getParameterByName('companyBankIds');

      const accountNumbers = Utils.getParameterByName('accountNumbers');

      const organisationNumber = Utils.getParameterByName('organisationNumber');

      await dispatch(
        signantActions.handleAuthorizeCompanyBanks({
          assignUser,
          companyId,
          requestID,
          trackingID,
          organisationNumber,
          companyBankIds,
          accountNumbers,
        }),
      );
    },
    [dispatch],
  );

  const getTextContent = useCallback(() => {
    switch (actionRef.current) {
      case 'payment-auth':
        return isLoading ? t('processing') : <span className={classes.accepted}>{t('signNext:payment-accepted')}</span>;

      case 'signant-auth':
        if (isLoading || isHandlingAuthorizeCompanyBanks) {
          return t('processing');
        }
        if (errorHandleAuthorizeCompanyBanks) {
          return <span className={classes.rejected}>{t('system:message.error')}</span>;
        }
        return <span className={classes.accepted}>{t('signNext:text-next')}</span>;
      default:
        return <span className={classes.accepted}>{t('signNext:text-next')}</span>;
    }
  }, [isLoading, isHandlingAuthorizeCompanyBanks, errorHandleAuthorizeCompanyBanks, t]);

  /**
   * componentDidMount
   */
  useEffect(() => {
    const action = Utils.getParameterByName('action');
    const bankGroup = Utils.getParameterByName('bankGroup');

    actionRef.current = action;

    switch (action) {
      case 'signant-auth':
        handleCompanyBankVerifyProcess({ bankGroup });
        break;

      case 'payment-auth':
        handlePaymentProcess();
        break;

      default:
        history.push(ErrorRoutes.Notfound);
        break;
    }
  }, [history, handleCompanyBankVerifyProcess, handlePaymentProcess]);

  /**
   * componentDidUpdate
   */
  const prevHandlingRef = useRef(isHandlingAuthorizeCompanyBanks);

  useEffect(() => {
    if (prevHandlingRef.current && !isHandlingAuthorizeCompanyBanks) {
      autoRedirectAfterCountdown();
    }

    prevHandlingRef.current = isHandlingAuthorizeCompanyBanks;
  }, [isHandlingAuthorizeCompanyBanks, autoRedirectAfterCountdown]);

  /**
   * componentWillUnmount
   */
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const txtContent = getTextContent();

  return (
    <div>
      <div
        className="row"
        style={{
          height: '100vh',
          display: 'flex',
          width: '30%',
        }}
      >
        <div
          className="col s12 text-center"
          style={{
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {txtContent}
          <div
            style={{
              minHeight: '24px',
              opacity: !isLoading && seconds > 0 && seconds <= 5 ? 1 : 0,
              transition: 'opacity 0.4s ease',
            }}
          >
            ({seconds})
          </div>
          <LoadingProgressBar loading={isLoading || isGettingAuthStatus || isHandlingAuthorizeCompanyBanks} />
          {authorizeCompanyBanksData?.accountNumbers && (
            <SignNextBankAuthorize t={t} authorizeCompanyBanksData={authorizeCompanyBanksData} />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    isGettingAuthStatus: state.signant.isGettingAuthStatus,
    isHandlingAuthorizeCompanyBanks: state.signant.isHandlingAuthorizeCompanyBanks,
    authorizeCompanyBanksData: state.signant.handleAuthorizeCompanyBanksData,
    errors: state.signant.errors,
  };
};

export default withRouter(connect(mapStateToProps)(withNamespaces(['signNext', 'system'])(SignNext)));
